import React from 'react'
import { Breadcrumb } from '../commons/Breadcrumb'
import { Faq } from '../commons/Faq'
import { HowToBook } from '../commons/service-hub/HowToBook'
import { WhyUs } from '../commons/service-hub/WhyUs'
import { AirconServices } from '../index/service-category/aircon-service/AirconServices'
import { AirconServiceMatcher } from '../index/service-category/aircon-service/AirconServiceMatcher'
import { AirconBrandsWeSupport } from '../index/service-category/aircon-service/AirconBrandsWeSupport'
import { AcServiceLocality } from '../index/service-category/aircon-service/AcServiceLocality'
import { MarketHead } from './MarketHead'
import { MarketBooking } from './MarketBooking'
import { IntroStrip, LinkCards, MarketSection } from './ui'
import { MarketAirconHero } from './MarketAirconHero'
import { ContentSection } from './AirconServicePage'
import { LocalRulesSection, MarketDealsSection, MarketPriceComparison, MarketProcessSection, MarketReviewsSection } from './AirconSections'
import { buildContext, guideLinks, resolveAll } from './content'
import { getAirconSections } from './sections'
import { FAQ_COUNTS, airconFaqBank, mergeFaqs } from './faqs'
import { breadcrumbNode, faqNode, graph, serviceNode, webPageNode } from '../../utils/market-schema'
import { capitalise, t } from '../../utils/markets'

// hreflang set shared by the three aircon hubs: Singapore, Dubai, Kuala Lumpur
// are the regional variants of one page. Abu Dhabi is not in the set - it is
// en-AE like Dubai, and a language/region can only point at one URL - so it
// stands on its own content (see the plan's anti-cannibalisation rules).
export const AIRCON_HUB_ALTERNATES = [
    { hreflang: 'en-sg', path: '/sg/aircon-servicing-singapore' },
    { hreflang: 'en-ae', path: '/ae/ac-service-dubai' },
    { hreflang: 'en-my', path: '/my/aircond-service-kuala-lumpur' },
]

/**
 * UAE / Malaysia aircon city hub. Same section order as the Singapore hub
 * (components/index/service-category/aircon-service/Aircon.js): hero, price
 * proof, deals, reviews, how to book, process, services, matcher, why us,
 * brands, areas, local rules, guides, FAQ - built from the same components,
 * with the market's copy and this city's own content module.
 */
export const AirconCityHub = ({ countryCd, citySlug, tables, requestCategorySettings, modifiedDate, testimonials = [], content: raw }) => {
    const ctx = buildContext({ countryCd, citySlug, tables })
    const { market, city } = ctx
    const content = resolveAll(raw, ctx)
    const sections = getAirconSections(ctx)
    const path = market.hubs.aircon[citySlug]
    const guides = guideLinks(market, citySlug, 'aircon')
    // The page's own questions first, then the market FAQ bank (Singapore-level depth).
    const faqs = mergeFaqs(FAQ_COUNTS.hub, content.faqs, airconFaqBank(ctx).hub)

    const jsonLd = graph(
        webPageNode({ market, path, title: content.title, description: content.description, dateModified: modifiedDate }),
        breadcrumbNode(path, content.h1),
        serviceNode({
            market,
            city,
            path,
            name: content.h1,
            serviceType: content.serviceType,
            description: content.description,
            offers: content.offers,
        }),
        faqs.length ? faqNode(path, faqs) : null,
    )

    return (
        <>
            <MarketHead
                market={market}
                path={path}
                title={content.title}
                description={content.description}
                jsonLd={jsonLd}
                alternates={content.alternates ?? []}
                ogImageAlt={content.imageAlt}
            />
            <MarketBooking market={market} city={city} requestType="Aircon Services" requestCategorySettings={requestCategorySettings}>
                <MarketAirconHero
                    market={market}
                    tables={tables}
                    panel={{ type: 'servicing', defaultMount: 'Wall' }}
                    title={content.h1}
                    subtitle={content.heroSubtitle || `Upfront prices across ${city.name}, travel included`}
                    image={content.image}
                    imageAlt={content.imageAlt}
                    pills={['Upfront prices', 'Travel included']}
                />
                <Breadcrumb pageTitle={content.h1} jsonLd={false} />
                <IntroStrip eyebrow={content.eyebrow} lead={content.lead} facts={content.facts} />

                <MarketPriceComparison copy={sections.priceComparison} tables={tables} priceHref={sections.priceHref} />
                <MarketDealsSection title={sections.dealsTitle} tables={tables} />
                <MarketReviewsSection testimonials={testimonials} />
                <HowToBook theme="light" title={sections.howToBook.title} steps={sections.howToBook.steps} />
                <MarketProcessSection service="general" copy={sections.process.general} />
                <AirconServices services={sections.serviceCards} title={sections.services.title} intro={sections.services.intro} />

                {(content.sections ?? []).map((section, i) => (
                    <ContentSection key={i} id={section.id} section={section} />
                ))}

                <AirconServiceMatcher
                    rows={sections.matcherRows}
                    title={sections.matcher.title}
                    intro={sections.matcher.intro}
                    caption={sections.matcher.caption}
                    footnote={sections.matcher.footnote}
                />
                <WhyUs id="ac-why-us" theme="dark" title={sections.whyUs.title} subtitle={sections.whyUs.subtitle} reasons={sections.whyUs.reasons} />
                <AirconBrandsWeSupport market={market} intro={sections.brands.intro} linked={false} exclude={['Prism+']} />
                <AcServiceLocality links={sections.localityLinks} title={sections.areas.title} />
                <LocalRulesSection copy={sections.localRules} />

                {guides.length > 0 && (
                    <MarketSection title={`${capitalise(t(market, 'acShort'))} guides for ${city.name}`} tone="muted">
                        <LinkCards links={guides} />
                    </MarketSection>
                )}

                {faqs.length > 0 && (
                    <section className="max-w-5xl mx-auto px-4 pb-10" aria-label="Frequently asked questions">
                        <Faq questionsAnswers={faqs} title={content.faqTitle || sections.faqTitle} showSearch={false} jsonLd={false} />
                    </section>
                )}
            </MarketBooking>
        </>
    )
}
